import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select, MultiSelect } from '..';
import { FiLayers, FiFileText, FiHash, FiMapPin, FiBriefcase } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';
import CBO_LIST from './cboData';

export default function PositionForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stations, setStations] = useState([]);

  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    description: '',
    cbo: '',
    serviceStationIds: [],
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [cboOptions, setCboOptions] = useState([]);

  useEffect(() => {
    if (initialData) {
      // Determine initial selection
      let ids = [];
      if (initialData.serviceStations && initialData.serviceStations.length > 0) {
        ids = initialData.serviceStations.map(s => String(s.id));
      } else if (initialData.service_station_id) {
        // Fallback for old single selection
        ids = [String(initialData.service_station_id)];
      } else {
        // If no specific stations and no old ID, assume "All" (Global)
        ids = ['all'];
      }

      const initialCompanyId = initialData.company_id || initialData.company?.id;

      setFormData({
        company_id: initialCompanyId ? String(initialCompanyId) : '',
        name: initialData.name || '',
        description: initialData.description || '',
        cbo: initialData.cbo || '',
        serviceStationIds: ids,
      });
      setSelectedCompanyIds(initialCompanyId ? [String(initialCompanyId)] : []);
    } else {
      const defaultCompanyId = companyId ? String(companyId) : '';
      setFormData(prev => ({
        ...prev,
        company_id: defaultCompanyId,
        serviceStationIds: ['all'],
      }));
      setSelectedCompanyIds(defaultCompanyId ? [defaultCompanyId] : []);
    }
  }, [initialData, companyId]);

  useEffect(() => {
    const options = CBO_LIST.map(item => ({
      value: item.code,
      label: `${item.code} - ${item.name}`,
    }));
    setCboOptions(options);
  }, []);

  const companyOptions = useMemo(() => {
    return companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
  }, [companies]);

  useEffect(() => {
    async function loadStations() {
      const targetCompanyId = formData.company_id || companyId;
      if (!targetCompanyId) {
        setStations([]);
        return;
      }

      try {
        const data = await Api.getServiceStations(targetCompanyId);
        if (Array.isArray(data)) {
          setStations(data);
        }
      } catch (err) {
        // Only show error if we have a company selected
        if (targetCompanyId) {
            toast.error('Erro ao carregar postos de serviço para seleção.');
        }
      }
    }

    loadStations();
  }, [formData.company_id, companyId]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function handleStationsChange(values) {
    // Logic for "All" option
    // If 'all' is selected, it should be the only one, or clear others?
    // If user clicks 'all', we select only 'all'.
    // If user clicks specific, we unselect 'all'.
    
    let newValues = [...values];
    
    // Check if 'all' was just added
    const wasAllSelected = formData.serviceStationIds.includes('all');
    const isAllSelected = newValues.includes('all');
    
    if (!wasAllSelected && isAllSelected) {
        // User just selected "All" -> Clear others
        newValues = ['all'];
    } else if (wasAllSelected && isAllSelected && newValues.length > 1) {
        // User selected something else while "All" was active -> Unselect "All"
        newValues = newValues.filter(v => v !== 'all');
    } else if (wasAllSelected && !isAllSelected) {
        // User unselected "All" -> Empty list (or keep empty)
    }

    // If nothing selected, maybe default back to 'all'? Or let it be empty?
    // If empty, it's effectively "None", which might be invalid or "Global".
    // Let's allow empty.
    
    setFormData(prev => ({ ...prev, serviceStationIds: newValues }));
  }

  function validateForm() {
    const messages = [];

    if (!formData.name || !formData.name.trim()) {
      messages.push('Informe o nome do cargo.');
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isEdit) {
      if (!formData.company_id) {
        toast.error('Selecione uma empresa para cadastrar cargos.');
        return;
      }
    } else {
      const cleanedCompanyIds = (selectedCompanyIds || []).filter(id => id !== 'all');
      if (cleanedCompanyIds.length === 0) {
        toast.error('Selecione ao menos uma empresa para cadastrar cargos.');
        return;
      }
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const payloadBase = {
        name: formData.name,
        description: formData.description || null,
        cbo: formData.cbo || null,
        serviceStationIds: formData.serviceStationIds,
      };

      let result;

      if (isEdit && initialData && initialData.id) {
        // Use original company ID to find the resource, as per vehicle pattern
        const originalCompanyId = initialData.company_id || initialData.company?.id || companyId;
        const payload = {
          ...payloadBase,
          company_id: formData.company_id || String(originalCompanyId || ''),
        };
        result = await Api.updatePosition(originalCompanyId, initialData.id, payload);
      } else {
        const cleanedCompanyIds = (selectedCompanyIds || []).filter(id => id !== 'all');
        const createdPositions = [];

        for (const companyIdToCreate of cleanedCompanyIds) {
          const payload = {
            ...payloadBase,
            company_id: companyIdToCreate,
          };
          // create one position per selected company
          // backend uses companyId in URL and ignores/overwrites company_id in body
          // but we still send it for compatibility
          // eslint-disable-next-line no-await-in-loop
          const created = await Api.createPosition(companyIdToCreate, payload);
          createdPositions.push(created);
        }

        result = createdPositions[0];
      }

      toast.success(isEdit ? 'Cargo atualizado com sucesso!' : 'Cargo criado com sucesso!');
      
      if (onSuccess) {
        onSuccess(result);
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.error || 'Erro ao salvar cargo.';
      toast.error(msg);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  const stationOptions = [
    { value: 'all', label: 'TODOS' },
    ...stations.map(station => ({
      value: String(station.id),
      label: station.nickname || station.name,
    })),
  ];

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>Dados do cargo</h3>
        <div className="form-grid">
          <div className="full-width">
            {isEdit ? (
              <Select
                id="company_id"
                label="Empresa *"
                placeholder="SELECIONE UMA EMPRESA..."
                value={formData.company_id}
                onChange={handleChange}
                icon={FiBriefcase}
                options={companyOptions}
                searchable
                disabled={true}
              />
            ) : (
              <MultiSelect
                id="companyIds"
                label="Empresas *"
                placeholder="SELECIONE AS EMPRESAS..."
                value={selectedCompanyIds}
                onChange={(newValues) => {
                  const allValue = 'all';
                  const currentSelected = selectedCompanyIds;
                  const isAllSelected = newValues.includes(allValue);
                  const wasAllSelected = currentSelected.includes(allValue);

                  let nextValues;

                  if (isAllSelected && !wasAllSelected) {
                    const allIds = companies.map(c => String(c.id));
                    nextValues = [allValue, ...allIds];
                  } else if (!isAllSelected && wasAllSelected) {
                    nextValues = [];
                  } else if (wasAllSelected && newValues.length < currentSelected.length) {
                    const remaining = newValues.filter(v => v !== allValue);
                    nextValues = remaining;
                  } else {
                    const specificIds = newValues.filter(v => v !== allValue);
                    const allIds = companies.map(c => String(c.id));
                    if (specificIds.length === allIds.length && allIds.length > 0) {
                      nextValues = [allValue, ...specificIds];
                    } else {
                      nextValues = specificIds;
                    }
                  }

                  setSelectedCompanyIds(nextValues);
                }}
                icon={FiBriefcase}
                options={[
                  { value: 'all', label: 'TODAS' },
                  ...companyOptions,
                ]}
                searchable
              />
            )}
          </div>
          <div className="full-width">
            <Input
              id="name"
              label="Nome do Cargo *"
              value={formData.name}
              onChange={handleChange}
              placeholder="Ex: Gerente de Posto"
              required
              icon={FiLayers}
            />
          </div>
          <div className="full-width">
            <Select
              id="cbo"
              label="CBO"
              value={formData.cbo}
              onChange={handleChange}
              placeholder="Pesquise por código ou nome do CBO"
              icon={FiHash}
              options={cboOptions}
              searchable
            />
          </div>
        </div>

        <h3 className="section-title">Descrição</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="description"
              label="Descrição do Cargo"
              value={formData.description}
              onChange={handleChange}
              placeholder="Breve descrição das responsabilidades..."
              icon={FiFileText}
            />
          </div>
        </div>
      </div>

      <div className="modal-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
